import * as React from 'react'
import classNames from 'classnames'
import { useDispatch } from 'react-redux'
import { AnyAction } from 'redux'
import { ThunkDispatch } from 'redux-thunk'
import { State } from 'store'
import { FormikProvider, useFormik } from 'formik'
import { useHistory, useLocation, useParams } from 'react-router-dom'
import { History } from 'history'
import { useHasFeature } from 'features'
import { MessageKind } from 'uiComponents/messages'
import { Option } from 'uiComponents/combobox/combobox'
import { useGetAllArticles, useGetAllCategories, useGetAllProductsForSelect } from 'products/redux'
import { useGetProductAdditionalInfoQuery } from 'products/reduxApi'
import { useGetTimeSlotGroupsQuery } from 'timeSlots/reduxQuery'
import { ArticleBusinessType } from 'admin/articleService'
import { delay } from 'utils'
import { CapacityMember, SaveResult } from 'capacity/api/capacityGateway'
import { capacityApiUtil, useGetCapacityQuery, useSaveCapacityMutation } from 'capacity/reduxQuery'
import { useCapacityProducts } from 'capacity/hooks/useCapacityProducts'
import { useTimeSlotGroupDetails } from 'capacity/hooks/useTimeSlotGroupDetails'
import { useLeaveGuard } from 'capacity/hooks/useLeaveGuard'
import { capacityPaths } from 'capacity/paths'
import { PRODUCTS, SAVE, SECTION, TABS } from 'capacity/copy'
import { t } from 'translations'
import { CapacityHealth } from 'capacity/types'
import { nextSort, sortRows, TableSort } from 'capacity/derive/tableSort'
import { mixedConsequence, windowSummary } from 'capacity/derive/consequence'
import { isResolved } from 'capacity/derive/takesUp'
import {
    CapacityDraft,
    draftAfterFailedCreate,
    draftReducer,
    emptyDraft,
    hasDraftChanges,
} from 'capacity/staging/capacityDraft'
import { buildSavePlan, confirmText, saveDisabledReason, saveLabel, saveParts } from 'capacity/staging/savePlan'
import { useUrlParam } from 'utils/useUrlParam'
import { UnderlineTab, UnderlineTabs } from 'uiComponents/tabs/underlineTabs'
import { capacityFormSchema, capacityToBaseline, capacityToFormValues } from './capacityFormSchema'
import { CapacityTopBar } from './capacityTopBar'
import { SaveArea } from './saveArea'
import { CapacityConfigCard } from './capacityConfigCard'
import { ProductsSection } from './productsSection'
import { LimitExceptionsSection } from './limitExceptionsSection'
import { LeaveDialog } from './leaveDialog'
import {
    addableFilter,
    buildRows,
    filterRows,
    groupIdsToFetch,
    presentTypes,
    ProductRowModel,
    readSaleFilter,
    readTypeFilter,
    rowSortValue,
    SaleFilter,
    TypeFilter,
} from './rowModel'
import './capacityPage.scss'

export interface CapacityPageProps {
    accountSlug: string
    history: History
    setActiveSection: (section: string, header: string) => void
    replaceMessages: (id: string, status: MessageKind, text: string) => void
    hideMessage: (id: string) => void
}

interface CapacityPageParams {
    id: string
}

export interface CapacityPageLocationState {
    pendingDraft?: CapacityDraft
}

const MESSAGE_VISIBLE_MS = 3000

interface Settling {
    result: SaveResult
    since: number | undefined
}

export function CapacityPage(props: CapacityPageProps) {
    const { accountSlug, setActiveSection, replaceMessages, hideMessage } = props
    const { id } = useParams<CapacityPageParams>()
    const history = useHistory()
    const dispatch = useDispatch<ThunkDispatch<State, unknown, AnyAction>>()
    const location = useLocation<CapacityPageLocationState | undefined>()
    const isNew = id === 'new'
    const capacityId = isNew ? null : id

    const capacityQuery = useGetCapacityQuery({ accountSlug, capacityId: id }, { skip: isNew })
    const capacity = isNew ? undefined : capacityQuery.data
    const loadingCapacity = !isNew && (capacityQuery.isLoading || (!capacity && !capacityQuery.isError))
    const baseline = React.useMemo(() => capacityToBaseline(capacity), [capacity])

    const initialValues = React.useMemo(() => capacityToFormValues(capacity), [capacity])
    const formik = useFormik({
        initialValues,
        enableReinitialize: true,
        validationSchema: capacityFormSchema,
        validateOnChange: true,
        onSubmit: () => undefined,
    })
    const { values } = formik

    const [draft, dispatchDraft] = React.useReducer(draftReducer, location.state?.pendingDraft ?? emptyDraft)
    const [confirming, setConfirming] = React.useState(false)
    const [settling, setSettling] = React.useState<Settling | null>(null)

    const products = useCapacityProducts(accountSlug, capacityId)
    const articles = useGetAllArticles()
    const categories = useGetAllCategories()
    const productOptions = useGetAllProductsForSelect()
    const { data: additionalInfo, isLoading: numericIdsLoading } = useGetProductAdditionalInfoQuery({
        account: accountSlug,
    })
    const groupsQuery = useGetTimeSlotGroupsQuery(accountSlug)
    const reportEnabled = useHasFeature('is_capacity_report_enabled')
    const [saveCapacity, saveState] = useSaveCapacityMutation()

    React.useEffect(() => {
        setActiveSection('inventory', isNew ? SECTION.newTitle : capacity?.name ?? SECTION.title)
    }, [isNew, capacity?.name])

    React.useEffect(() => {
        if (capacityQuery.isError) {
            replaceMessages('server_error', 'error', t('capacity.page.loadError'))
        }
    }, [capacityQuery.isError])

    const articlesById = React.useMemo(
        () => Object.fromEntries(articles.map((article) => [article.id, article])),
        [articles],
    )
    const numericIdByUuid = React.useMemo(
        () => new Map((additionalInfo ?? []).map((info) => [info.uuid, info.id])),
        [additionalInfo],
    )
    const todayDate = React.useMemo(() => new Date(), [])

    const baseGroups = groupsQuery.data?.groups ?? null
    const neededGroupIds = React.useMemo(() => {
        if (!baseGroups || groupsQuery.data?.slotsIncluded) {
            return []
        }
        const staged = Object.values(draft.adds).map((add) => add.member)
        return groupIdsToFetch([...products.members, ...staged], baseGroups, categories)
    }, [baseGroups, groupsQuery.data?.slotsIncluded, categories, products.members, draft.adds])
    const groupDetails = useTimeSlotGroupDetails(accountSlug, neededGroupIds)
    const timeSlotGroups = React.useMemo(
        () =>
            baseGroups
                ? baseGroups.map((group) => {
                      const detail = groupDetails[group.id]
                      return detail?.status === 'ready' && detail.group ? { ...group, ...detail.group } : group
                  })
                : null,
        [baseGroups, groupDetails],
    )

    const allRows = React.useMemo(
        () =>
            buildRows({
                accountSlug,
                draft,
                members: products.members,
                articlesById,
                categories,
                timeSlotGroups: groupsQuery.isError ? 'unavailable' : timeSlotGroups,
                countingType: values.countingType,
                today: todayDate,
            }),
        [
            accountSlug,
            draft,
            products.members,
            articlesById,
            categories,
            timeSlotGroups,
            groupsQuery.isError,
            values.countingType,
            todayDate,
        ],
    )

    type PageTab = 'details' | 'exceptions'
    const [tabParam, setTabParam] = useUrlParam('tab')
    const activeTab: PageTab = tabParam === 'exceptions' && !isNew ? 'exceptions' : 'details'
    const pageTabs: UnderlineTab<PageTab>[] = [
        { id: 'details', label: TABS.details },
        {
            id: 'exceptions',
            label: TABS.exceptions,
            badge: capacity ? capacity.availabilityRules?.length ?? 0 : undefined,
            disabled: isNew,
            disabledReason: isNew ? TABS.exceptionsSaveFirst : undefined,
        },
    ]
    const [typeParam, setTypeParam] = useUrlParam('type')
    const [saleParam, setSaleParam] = useUrlParam('sale')
    const typeFilter = readTypeFilter(typeParam)
    const saleFilter = readSaleFilter(saleParam)
    const [sort, setSort] = React.useState<TableSort | null>(null)
    const visibleRows = React.useMemo(
        () => sortRows(filterRows(allRows, typeFilter, saleFilter), sort, rowSortValue),
        [allRows, typeFilter, saleFilter, sort],
    )

    const addOptions = React.useMemo<Option[]>(() => {
        const businessTypeById: Record<string, ArticleBusinessType | undefined> = {}
        const bundleIds = new Set<string>()
        for (const article of articles) {
            businessTypeById[article.id] = article.businessType
            if (article.isBundle || article.businessType === 'bundle') {
                bundleIds.add(article.id)
            }
        }
        const addable = addableFilter(allRows, businessTypeById, bundleIds)
        return productOptions
            .filter((option) => !addable.excluded(option.value))
            .map((option) => {
                const disabledReason = addable.disabledReason(option.value)
                return disabledReason ? { ...option, disabledReason } : option
            })
    }, [productOptions, articles, allRows])

    const consequence = React.useMemo(() => {
        const counted = allRows.filter((row) => !row.isOptionItem && !row.isBundle && row.status !== 'removing')
        if (counted.some((row) => !isResolved(row.takesUp))) {
            return null
        }
        const summary = windowSummary(
            counted.map((row) => ({
                name: row.member.name,
                window: isResolved(row.takesUp) && row.takesUp.sources.length > 0 ? 'slot' : 'day',
            })),
        )
        return mixedConsequence(values.countingType, summary)
    }, [allRows, values.countingType])

    const stageAdd = (productId: string) => {
        const article = articlesById[productId]
        const member: CapacityMember = {
            kind: 'product',
            id: productId,
            name: productOptions.find((option) => option.value === productId)?.label ?? productId,
            businessType: article?.businessType ?? null,
            isBundle: !!article?.isBundle,
            categoryIds: (article?.categories ?? []).map((category) => category.id),
            inactiveCategoryIds: (article?.inactiveCategories ?? []).map((category) => category.id),
            links: [],
        }
        dispatchDraft({ type: 'stageAdd', member })
    }

    const changeAmount = (row: ProductRowModel, next: number) => {
        if (row.status === 'new') {
            dispatchDraft({ type: 'setAddAmount', productId: row.member.id, amount: next })
        } else {
            dispatchDraft({ type: 'setAmount', productId: row.member.id, amount: next, baseline: row.savedAmount ?? 1 })
        }
    }

    const planInput = { baseline, values, draft, members: products.members }
    const parts = saveParts(planInput)
    const disabledReason = saveDisabledReason(planInput)
    const hasChanges =
        hasDraftChanges(draft) || (isNew ? values.name.trim() !== '' || values.limit.trim() !== '' : formik.dirty)

    const busy = saveState.isLoading || !!settling
    const leaveGuard = useLeaveGuard(hasChanges && !busy)

    React.useEffect(() => {
        if (location.state?.pendingDraft) {
            leaveGuard.allow(() =>
                history.replace({ pathname: location.pathname, search: location.search, hash: location.hash }),
            )
        }
    }, [])

    const showMessage = async (kind: MessageKind, text: string) => {
        const id = kind === 'success' ? 'success' : 'server_error'
        replaceMessages(id, kind, text)
        await delay(MESSAGE_VISIBLE_MS)
        hideMessage(id)
    }

    const reportResult = (result: SaveResult) => {
        const name = values.name.trim()
        if (result.failed.length > 0) {
            showMessage('error', SAVE.partlyFailed(result.failed.map((entry) => entry.op.label)))
        } else {
            showMessage('success', result.created ? SAVE.created(name) : SAVE.saved(name))
        }
    }

    React.useEffect(() => {
        if (!settling || (products.updatedAt === settling.since && products.status !== 'error')) {
            return
        }
        dispatchDraft({ type: 'commitSucceeded', ops: settling.result.succeeded })
        dispatchDraft({ type: 'markFailed', failed: settling.result.failed })
        setSettling(null)
        setConfirming(false)
        reportResult(settling.result)
    }, [settling, products.updatedAt, products.status])

    const onConfirm = async () => {
        const plan = buildSavePlan({ accountSlug, capacityId, ...planInput })
        const response = await saveCapacity({ accountSlug, plan })
        const result = 'data' in response ? response.data : undefined
        if (!result || !result.capacitySaved || !result.capacityId) {
            setConfirming(false)
            showMessage('error', SAVE.failed)
            return
        }
        if (plan.capacity?.kind === 'update') {
            const saved = plan.capacity.body
            dispatch(
                capacityApiUtil.updateQueryData('getCapacity', { accountSlug, capacityId: saved.id }, (current) => ({
                    ...current,
                    ...saved,
                })),
            )
        }
        if (plan.capacity) {
            formik.resetForm({ values })
        }
        if (result.created) {
            reportResult(result)
            const state: CapacityPageLocationState = { pendingDraft: draftAfterFailedCreate(draft, result.failed) }
            leaveGuard.allow(() => history.push(capacityPaths.item(accountSlug, result.capacityId as string), state))
            return
        }
        if (result.succeeded.length === 0) {
            dispatchDraft({ type: 'markFailed', failed: result.failed })
            setConfirming(false)
            reportResult(result)
            return
        }
        setSettling({ result, since: products.updatedAt })
    }

    const onSaveClick = async () => {
        const errors = await formik.validateForm()
        if (Object.keys(errors).length > 0) {
            formik.setTouched({ name: true, limit: true, countingType: true })
            return
        }
        setConfirming(true)
    }

    const health: CapacityHealth = 'unknown'
    const takesUpTooltip = PRODUCTS.takesUpTooltip

    return (
        <FormikProvider value={formik}>
            <div className="capacity-page">
                {leaveGuard.asking && <LeaveDialog onStay={leaveGuard.stay} onLeave={leaveGuard.leave} />}
                <CapacityTopBar
                    backTo={capacityPaths.list(accountSlug)}
                    reportTo={!isNew && reportEnabled ? capacityPaths.capacityReport(accountSlug) : undefined}
                >
                    {!loadingCapacity && !capacityQuery.isError && (
                        <SaveArea
                            label={saveLabel(parts, isNew)}
                            disabledReason={disabledReason}
                            confirming={confirming}
                            confirmText={confirmText(parts, isNew, values.name.trim())}
                            busy={busy}
                            onSave={onSaveClick}
                            onConfirm={onConfirm}
                            onKeepEditing={() => setConfirming(false)}
                        />
                    )}
                </CapacityTopBar>
                <UnderlineTabs
                    tabs={pageTabs}
                    value={activeTab}
                    onChange={(next) => setTabParam(next === 'details' ? null : next)}
                    ariaLabel={TABS.label}
                    idPrefix="capacity"
                    className="capacity-page-tabs"
                />
                <div className="capacity-page-panels">
                    <div
                        role="tabpanel"
                        id="capacity-panel-details"
                        aria-labelledby="capacity-tab-details"
                        aria-hidden={activeTab !== 'details'}
                        className={classNames('capacity-page-panel', { 'is-hidden': activeTab !== 'details' })}
                    >
                        <CapacityConfigCard loading={loadingCapacity} isNew={isNew} health={health} />
                        <ProductsSection
                            status={products.status}
                            onRetry={products.refetch}
                            optionItems={products.optionItems}
                            allRows={allRows}
                            visibleRows={visibleRows}
                            sort={sort}
                            onSort={(key) => setSort((current) => nextSort(current, key))}
                            addOptions={addOptions}
                            onAdd={stageAdd}
                            types={presentTypes(allRows)}
                            typeFilter={typeFilter}
                            onTypeFilter={(next: TypeFilter) => setTypeParam(next === 'all' ? null : next)}
                            saleFilter={saleFilter}
                            onSaleFilter={(next: SaleFilter) => setSaleParam(next === 'all' ? null : next)}
                            onToggle={(productId) => dispatchDraft({ type: 'toggleRemoval', productId })}
                            onAmountChange={changeAmount}
                            numericIdOf={(productId) => numericIdByUuid.get(productId)}
                            numericIdsLoading={numericIdsLoading}
                            takesUpTooltip={takesUpTooltip}
                            consequence={consequence}
                        />
                    </div>
                    {capacity && (
                        <div
                            role="tabpanel"
                            id="capacity-panel-exceptions"
                            aria-labelledby="capacity-tab-exceptions"
                            aria-hidden={activeTab !== 'exceptions'}
                            className={classNames('capacity-page-panel', { 'is-hidden': activeTab !== 'exceptions' })}
                        >
                            <LimitExceptionsSection
                                accountSlug={accountSlug}
                                capacityId={capacity.id}
                                rules={capacity.availabilityRules ?? []}
                            />
                        </div>
                    )}
                </div>
            </div>
        </FormikProvider>
    )
}

export default CapacityPage
